import type { QueryClient } from '@tanstack/react-query';
import {
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
  useLocation,
} from '@tanstack/react-router';
import { Suspense, lazy, useEffect, type ComponentType } from 'react';
import {
  FOLDERS_FEATURE_ENABLED,
  ADMIN_PANEL_ENABLED,
  STORYBOARDS_FEATURE_ENABLED,
  WORKSPACE_FEATURE_ENABLED,
} from './config';
import { LandingPage } from './modules/marketing/components/LandingPage';
import { capturePageView } from './lib/analytics';
import { sanitizeRedirect } from './modules/auth/auth-storage';
import { STUDIO_CANVAS_ENABLED } from './modules/studio/constants';
import { ShutdownBanner } from './modules/shutdown/ShutdownBanner';

const AnalyticsRouteTracker = () => {
  const location = useLocation();

  useEffect(() => {
    capturePageView(location.pathname);
  }, [location.pathname]);

  return null;
};

const RootComponent = () => (
  <>
    <AnalyticsRouteTracker />
    <ShutdownBanner />
    <Outlet />
  </>
);
const STUDIO_DEFAULT_SEARCH = {
  canvasAssetId: undefined,
  jobId: undefined,
  outputIndex: undefined,
};

const FirebaseFallback = () => {
  if (typeof window !== 'undefined') {
    window.location.replace('/?redirect=/studio');
  }
  return null;
};

const lazyRoute = <TModule extends Record<string, unknown>>(
  load: () => Promise<TModule>,
  exportName: keyof TModule
) => {
  const LazyComponent = lazy(async () => {
    const module = await load();
    const component = module[exportName] as ComponentType | undefined;
    if (!component) {
      throw new Error(`Missing route export: ${String(exportName)}`);
    }
    return { default: component };
  });

  const RouteComponent = () => (
    <Suspense fallback={null}>
      <LazyComponent />
    </Suspense>
  );

  RouteComponent.displayName = `LazyRoute(${String(exportName)})`;
  return RouteComponent;
};

const AuthLayout = lazyRoute(() => import('./modules/auth/components/AuthLayout'), 'AuthLayout');
const ProtectedAppLayout = lazyRoute(
  () => import('./app/layouts/ProtectedAppLayout'),
  'ProtectedAppLayout'
);
const SignInPage = lazyRoute(() => import('./modules/auth/components/SignInPage'), 'SignInPage');
const BillingDashboard = lazyRoute(
  () => import('./modules/billing/components/BillingDashboard'),
  'BillingDashboard'
);
const MoyasarTokenReturn = lazyRoute(
  () => import('./modules/billing/components/MoyasarTokenReturn'),
  'MoyasarTokenReturn'
);
const WalletPaymentReturn = lazyRoute(
  () => import('./modules/billing/components/WalletPaymentReturn'),
  'WalletPaymentReturn'
);
const PrivacyPolicyPage = lazyRoute(
  () => import('./modules/marketing/components/PrivacyPolicyPage'),
  'PrivacyPolicyPage'
);
const TermsOfServicePage = lazyRoute(
  () => import('./modules/marketing/components/TermsOfServicePage'),
  'TermsOfServicePage'
);
const ClosurePage = lazyRoute(
  () => import('./modules/shutdown/ClosurePage'),
  'ClosurePage'
);
const AccountDeletionCancelPage = lazyRoute(
  () => import('./modules/settings/routes/AccountDeletionCancelPage'),
  'AccountDeletionCancelPage'
);
const AccountDeletionRequestedPage = lazyRoute(
  () => import('./modules/settings/routes/AccountDeletionRequestedPage'),
  'AccountDeletionRequestedPage'
);
const AccountSettingsPage = lazyRoute(
  () => import('./modules/settings/routes/AccountSettingsPage'),
  'AccountSettingsPage'
);
const AssistantPage = lazyRoute(() => import('./modules/assistant'), 'AssistantPage');
const AdminAccessDenied = lazyRoute(
  () => import('./modules/admin/components/AdminAccessDenied'),
  'AdminAccessDenied'
);
const AdminDashboard = lazyRoute(
  () => import('./modules/admin/components/AdminDashboard'),
  'AdminDashboard'
);
const StudioCanvasPage = lazyRoute(() => import('./modules/studio/canvas'), 'StudioCanvasPage');
const StudioDashboard = lazyRoute(
  () => import('./modules/studio/components/StudioDashboard'),
  'StudioDashboard'
);
const StoryboardDetailPage = lazyRoute(
  () => import('./modules/studio/storyboards'),
  'StoryboardDetailPage'
);
const StoryboardNewPage = lazyRoute(
  () => import('./modules/studio/storyboards'),
  'StoryboardNewPage'
);
const StoryboardsPage = lazyRoute(() => import('./modules/studio/storyboards'), 'StoryboardsPage');
const AcceptInvitePage = lazyRoute(
  () => import('./modules/workspace/components/AcceptInvitePage'),
  'AcceptInvitePage'
);
const BrandKitsPage = lazyRoute(
  () => import('./modules/workspace/components/BrandKitsPage'),
  'BrandKitsPage'
);
const FoldersPage = lazyRoute(
  () => import('./modules/workspace/components/FoldersPage'),
  'FoldersPage'
);
const InvitesPage = lazyRoute(
  () => import('./modules/workspace/components/InvitesPage'),
  'InvitesPage'
);
const MembersPage = lazyRoute(
  () => import('./modules/workspace/components/MembersPage'),
  'MembersPage'
);
const WorkspaceLayout = lazyRoute(
  () => import('./modules/workspace/components/WorkspaceLayout'),
  'WorkspaceLayout'
);

const rootRoute = createRootRoute({
  component: RootComponent,
});

const landingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: typeof search.redirect === 'string' ? sanitizeRedirect(search.redirect) : undefined,
  }),
  component: LandingPage,
});

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'terms',
  component: TermsOfServicePage,
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'privacy',
  component: PrivacyPolicyPage,
});

const closureRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'closure',
  component: ClosurePage,
});

const accountDeletionRequestedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'account/deletion/requested',
  component: AccountDeletionRequestedPage,
  validateSearch: (search: Record<string, unknown>) => ({
    effectiveAt: typeof search.effectiveAt === 'string' ? search.effectiveAt : undefined,
  }),
});

const accountDeletionCancelRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'account/deletion/cancel',
  component: AccountDeletionCancelPage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
});

const acceptInviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'invites/$inviteId/accept',
  component: AcceptInvitePage,
  beforeLoad: () => {
    if (!WORKSPACE_FEATURE_ENABLED) {
      throw redirect({ to: '/studio', search: STUDIO_DEFAULT_SEARCH });
    }
  },
});

const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'auth',
  component: AuthLayout,
});

const signInRoute = createRoute({
  getParentRoute: () => authRoute,
  path: 'sign-in',
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: typeof search.redirect === 'string' ? sanitizeRedirect(search.redirect) : undefined,
  }),
  beforeLoad: ({ search }) => {
    throw redirect({
      to: '/',
      search: {
        redirect: search.redirect,
      },
    });
  },
  component: SignInPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app-layout',
  component: ProtectedAppLayout,
  beforeLoad: async ({ location }) => {
    const { ensureAuthResolved } = await import('./modules/auth/AuthProvider');
    const snapshot = await ensureAuthResolved();

    if (snapshot.status !== 'authenticated') {
      const redirectTarget =
        typeof location.href === 'string'
          ? location.href
          : `${location.pathname ?? '/'}${(location as { searchStr?: string }).searchStr ?? ''}${
              location.hash ?? ''
            }`;
      throw redirect({
        to: '/',
        search: {
          redirect: redirectTarget,
        },
      });
    }
  },
});

const studioLayoutRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'studio',
  component: () => <Outlet />,
});

const studioRoute = createRoute({
  getParentRoute: () => studioLayoutRoute,
  path: '/',
  component: StudioDashboard,
  validateSearch: (search: Record<string, unknown>) => {
    const canvasAssetId =
      typeof search.canvasAssetId === 'string' ? search.canvasAssetId : undefined;
    const jobId = typeof search.jobId === 'string' ? search.jobId : undefined;
    const rawOutputIndex = search.outputIndex;
    const parsedOutputIndex =
      typeof rawOutputIndex === 'number'
        ? rawOutputIndex
        : typeof rawOutputIndex === 'string' && rawOutputIndex.trim().length > 0
          ? Number(rawOutputIndex)
          : undefined;
    return {
      canvasAssetId,
      jobId,
      outputIndex: Number.isFinite(parsedOutputIndex) ? parsedOutputIndex : undefined,
    };
  },
});

const studioCanvasLayoutRoute = createRoute({
  getParentRoute: () => studioLayoutRoute,
  path: 'canvas',
  component: () => <Outlet />,
  beforeLoad: () => {
    if (!STUDIO_CANVAS_ENABLED) {
      throw redirect({ to: '/studio', search: STUDIO_DEFAULT_SEARCH });
    }
  },
});

const studioStoryboardsLayoutRoute = createRoute({
  getParentRoute: () => studioLayoutRoute,
  path: 'storyboards',
  component: () => <Outlet />,
  beforeLoad: () => {
    if (!STORYBOARDS_FEATURE_ENABLED) {
      throw redirect({ to: '/studio', search: STUDIO_DEFAULT_SEARCH });
    }
  },
});

const studioCanvasRoute = createRoute({
  getParentRoute: () => studioCanvasLayoutRoute,
  path: '/',
  component: StudioCanvasPage,
});

const studioCanvasAssetRoute = createRoute({
  getParentRoute: () => studioCanvasLayoutRoute,
  path: '$assetId',
  component: StudioCanvasPage,
});

const storyboardsRoute = createRoute({
  getParentRoute: () => studioStoryboardsLayoutRoute,
  path: '/',
  component: StoryboardsPage,
});

const storyboardNewRoute = createRoute({
  getParentRoute: () => studioStoryboardsLayoutRoute,
  path: 'new',
  component: StoryboardNewPage,
});

const storyboardDetailRoute = createRoute({
  getParentRoute: () => studioStoryboardsLayoutRoute,
  path: '$storyboardId',
  component: () => <StoryboardDetailPage />,
});

const billingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'billing',
  component: BillingDashboard,
  validateSearch: (search: Record<string, unknown>) => ({
    section: typeof search.section === 'string' ? search.section : undefined,
  }),
});

const walletPaymentReturnRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'billing/payment-return',
  component: WalletPaymentReturn,
});

const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'profile',
  component: AccountSettingsPage,
});

const assistantRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'assistant',
  component: AssistantPage,
});

const adminRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'admin',
  component: AdminDashboard,
  beforeLoad: async () => {
    if (!ADMIN_PANEL_ENABLED) {
      throw redirect({ to: '/studio', search: STUDIO_DEFAULT_SEARCH });
    }
    const { ensureAuthResolved } = await import('./modules/auth/AuthProvider');
    const snapshot = await ensureAuthResolved();
    if (snapshot.status !== 'authenticated' || snapshot.user?.role !== 'admin') {
      throw redirect({
        to: '/admin/access-denied',
      });
    }
  },
});

const adminAccessDeniedRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'admin/access-denied',
  component: AdminAccessDenied,
});

const legacySettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'settings',
  component: () => <Outlet />,
});

const legacySettingsIndexRoute = createRoute({
  getParentRoute: () => legacySettingsRoute,
  path: '/',
  component: () => null,
  beforeLoad: () => {
    throw redirect({ to: '/profile' });
  },
});

const legacySettingsAccountRoute = createRoute({
  getParentRoute: () => legacySettingsRoute,
  path: 'account',
  component: () => null,
  beforeLoad: () => {
    throw redirect({ to: '/profile' });
  },
});

const legacySettingsUsageRoute = createRoute({
  getParentRoute: () => legacySettingsRoute,
  path: 'usage',
  component: () => null,
  beforeLoad: () => {
    throw redirect({ to: '/billing', search: { section: 'wallet' } });
  },
});

const workspaceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: 'workspace',
  component: WorkspaceLayout,
  beforeLoad: () => {
    if (!WORKSPACE_FEATURE_ENABLED) {
      throw redirect({ to: '/studio', search: STUDIO_DEFAULT_SEARCH });
    }
  },
});

const workspaceFoldersRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: '/',
  component: FoldersPage,
  beforeLoad: () => {
    if (!FOLDERS_FEATURE_ENABLED) {
      throw redirect({ to: '/studio', search: STUDIO_DEFAULT_SEARCH });
    }
  },
});

const workspaceBrandKitsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'brand-kits',
  component: BrandKitsPage,
});

const workspaceMembersRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'members',
  component: MembersPage,
});

const workspaceInvitesRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: 'invites',
  component: InvitesPage,
});

const firebaseFallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '__',
  component: () => <Outlet />,
});

const moyasarCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'billing/token-callback',
  component: MoyasarTokenReturn,
});

const firebaseAuthRoute = createRoute({
  getParentRoute: () => firebaseFallbackRoute,
  path: 'auth',
  component: () => <Outlet />,
});

const firebaseHandlerRoute = createRoute({
  getParentRoute: () => firebaseAuthRoute,
  path: 'handler',
  component: FirebaseFallback,
});

const firebaseActionRoute = createRoute({
  getParentRoute: () => firebaseAuthRoute,
  path: 'action',
  component: FirebaseFallback,
});

const routeTree = rootRoute.addChildren([
  landingRoute,
  termsRoute,
  privacyRoute,
  closureRoute,
  accountDeletionRequestedRoute,
  accountDeletionCancelRoute,
  acceptInviteRoute,
  authRoute.addChildren([signInRoute]),
  appRoute.addChildren([
    studioLayoutRoute.addChildren([
      studioRoute,
      studioCanvasLayoutRoute.addChildren([studioCanvasRoute, studioCanvasAssetRoute]),
      studioStoryboardsLayoutRoute.addChildren([
        storyboardsRoute,
        storyboardNewRoute,
        storyboardDetailRoute,
      ]),
    ]),
    profileRoute,
    billingRoute,
    walletPaymentReturnRoute,
    assistantRoute,
    workspaceRoute.addChildren([
      workspaceFoldersRoute,
      workspaceBrandKitsRoute,
      workspaceMembersRoute,
      workspaceInvitesRoute,
    ]),
    adminRoute,
    adminAccessDeniedRoute,
    legacySettingsRoute.addChildren([
      legacySettingsIndexRoute,
      legacySettingsAccountRoute,
      legacySettingsUsageRoute,
    ]),
  ]),
  moyasarCallbackRoute,
  firebaseFallbackRoute.addChildren([
    firebaseAuthRoute.addChildren([firebaseHandlerRoute, firebaseActionRoute]),
  ]),
]);

if (import.meta.env.DEV && studioLayoutRoute.fullPath) {
  console.debug('[router] studio route path', studioLayoutRoute.fullPath);
}

export const routerSingleton = createRouter({
  routeTree,
  context: {
    queryClient: undefined as unknown as QueryClient,
  },
});

export const createAppRouter = (queryClient: QueryClient) => {
  routerSingleton.update({
    context: {
      queryClient,
    },
  });
  return routerSingleton;
};

type AppRouter = typeof routerSingleton;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}

export const RouterRoot = ({ router }: { router: AppRouter }) => <RouterProvider router={router} />;
